import { ArrayNotEmpty, IsNotEmpty, Max, Min, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';
import { TriggerType } from './trigger';

export enum ActionType {
  AddNoReplyNeeded = 'addNoReplyNeeded',
  AddTopics = 'addTopics',
  CloseConversation = 'closeConversation',
  CreateTask = 'createTask',
  RouteToAgent = 'routeToAgent',
  RouteToGroup = 'routeToGroup',
  SendReply = 'sendReply',
  SetPriority = 'setPriority',
  UnassignAgent = 'unassignAgent',
}

export default class Actions {
  addNoReplyNeeded?: boolean;
  @ValidateNested({ each: true })
  @Type(() => AddTopicsAction)
  addTopics?: AddTopicsAction;
  @ValidateNested({ each: true })
  @Type(() => CreateTaskAction)
  createTask?: CreateTaskAction;
  closeConversation?: boolean;
  @ValidateNested({ each: true })
  @Type(() => RouteToGroupAction)
  routeToGroup?: RouteToGroupAction;
  @ValidateNested({ each: true })
  @Type(() => RouteToAgentAction)
  routeToAgent?: RouteToAgentAction;
  @ValidateNested({ each: true })
  @Type(() => SendReplyAction)
  sendReply?: SendReplyAction;
  @ValidateNested({ each: true })
  @Type(() => SetPriorityAction)
  setPriority?: SetPriorityAction;
  unassignAgent?: boolean;

  constructor(attr = {} as any) {
    for (const prop in attr) {
      this[prop] = attr[prop];
    }
  }
}

export class AddTopicsAction {
  @ArrayNotEmpty({ message: 'Please select at least one topic' })
  topicIds: string[];

  constructor({ topicIds }: { topicIds: string[] } = { topicIds: [] }) {
    this.topicIds = topicIds;
  }

  updateTopic(topicId) {
    // remove if present, add if not present
    const index = this.topicIds.indexOf(topicId);
    index === -1 ? this.topicIds.push(topicId) : this.topicIds.splice(index, 1);
  }
}

class Assignee {
  agentId?: string;
  @IsNotEmpty({ message: 'Please assign to an inbox' })
  routingGroupId?: string;

  setAgentId(agentId) {
    this.agentId = agentId;
  }

  setRoutingGroupId(routingGroupId) {
    this.routingGroupId = routingGroupId;
  }

  constructor({ agentId, routingGroupId }: { agentId?: string; routingGroupId?: string } = {}) {
    this.agentId = agentId;
    this.routingGroupId = routingGroupId;
  }
}

export class CreateTaskAction {
  @ValidateNested({ each: true })
  @Type(() => Assignee)
  assignee: Assignee;
  @Min(0, { message: 'Please set due in value to positive value' })
  @Max(Number.MAX_SAFE_INTEGER)
  dueInMinutes: number;
  @IsNotEmpty({ message: 'Please add a description for the task' })
  note: string;

  constructor(
    {
      assignee,
      dueInMinutes,
      note,
    }: { assignee?: { agentId: string; routingGroupId: string }; dueInMinutes: number; note: string } = {
      dueInMinutes: 0,
      note: '',
    }
  ) {
    const assigneeModel = assignee === undefined ? new Assignee() : new Assignee(assignee);
    this.assignee = assigneeModel;
    this.dueInMinutes = dueInMinutes;
    this.note = note;
  }

  updateAssignee(assignee: Assignee) {
    this.assignee = assignee;
  }

  updateDueInMinutes(dueInMinutes: number) {
    this.dueInMinutes = dueInMinutes;
  }

  updateNote(note: string) {
    this.note = note;
  }
}

export enum AnswerType {
  AnyChannel = 'anyChannel',
  Info = 'info',
  Message = 'message',
  SelfService = 'selfService',
}

export class SendReplyAction {
  @IsNotEmpty({ message: 'Please select an answer' })
  snippetId?: string;
  @IsNotEmpty({ message: 'Please select an answer channel' })
  snippetType?: AnswerType;
  @IsNotEmpty({ message: 'Please select an answer language' })
  snippetLanguage?: string;

  constructor(
    {
      snippetId,
      snippetType,
      snippetLanguage,
    }: {
      snippetId?: string;
      snippetType?: AnswerType;
      snippetLanguage?: string;
    } = { snippetId: undefined, snippetType: undefined, snippetLanguage: undefined }
  ) {
    this.snippetId = snippetId;
    this.snippetType = snippetType;
    this.snippetLanguage = snippetLanguage;
  }

  updateSnippetId(snippetId?: string) {
    this.snippetId = snippetId;
  }

  updateSnippetType(snippetType?: AnswerType) {
    this.snippetType = snippetType;
  }

  updateSnippetLanguage(snippetLanguage?: string) {
    this.snippetLanguage = snippetLanguage;
  }
}

export enum RoutingPriority {
  Low = 1,
  Medium = 3,
  High = 5,
  Extreme = 10,
}

export class SetPriorityAction {
  priority: RoutingPriority;

  constructor({ priority }: { priority: RoutingPriority } = { priority: RoutingPriority.Low }) {
    this.priority = priority;
  }

  updatePriority(priority) {
    this.priority = priority;
  }
}
export class RouteToAgentAction {
  @IsNotEmpty({ message: 'Please select an agent' })
  agentId?: string;

  constructor({ agentId }: { agentId?: string } = { agentId: undefined }) {
    this.agentId = agentId;
  }

  updateAgent(agentId) {
    this.agentId = agentId;
  }
}

export class RouteToGroupAction {
  @IsNotEmpty({ message: 'Please select an inbox' })
  routingGroupId?: string;

  constructor({ routingGroupId }: { routingGroupId?: string } = { routingGroupId: undefined }) {
    this.routingGroupId = routingGroupId;
  }

  updateGroup(routingGroupId) {
    this.routingGroupId = routingGroupId;
  }
}

export function actionsFromDto(dto): Actions {
  const actions = {};
  for (const action in dto) {
    const type = action as ActionType;
    actions[type] = actionFromDto({ type, value: dto[type] });
  }
  return new Actions(actions);
}

export function actionFromDto(dto: {
  type: ActionType;
  value?: any;
}):
  | AddTopicsAction
  | CreateTaskAction
  | RouteToAgentAction
  | RouteToGroupAction
  | SendReplyAction
  | SetPriorityAction
  | boolean {
  switch (dto.type) {
    case ActionType.AddTopics:
      return new AddTopicsAction(dto.value);
    case ActionType.CreateTask:
      return new CreateTaskAction(dto.value);
    case ActionType.RouteToAgent:
      return new RouteToAgentAction(dto.value);
    case ActionType.RouteToGroup:
      return new RouteToGroupAction(dto.value);
    case ActionType.SendReply:
      return new SendReplyAction(dto.value);
    case ActionType.SetPriority:
      return new SetPriorityAction(dto.value);
    default:
      // boolean values are always true (key is omitted otherwise)
      return true;
  }
}

export const invalidActionsForTrigger: { [key: string]: ActionType[] } = {
  [TriggerType.AgentStatusUpdate]: [ActionType.CreateTask, ActionType.SendReply, ActionType.SetPriority],
  [TriggerType.AnswerUsed]: [ActionType.SendReply, ActionType.SetPriority],
  [TriggerType.ConversationDueIn]: [ActionType.CreateTask, ActionType.SendReply, ActionType.SetPriority],
  [TriggerType.IncomingCommunication]: [ActionType.SetPriority],
  [TriggerType.SmartMatch]: [
    ActionType.AddNoReplyNeeded,
    ActionType.AddTopics,
    ActionType.CloseConversation,
    ActionType.CreateTask,
    ActionType.RouteToAgent,
    ActionType.RouteToGroup,
    ActionType.SendReply,
    ActionType.UnassignAgent,
  ],
  [TriggerType.TaskDueIn]: [
    ActionType.AddTopics,
    ActionType.AddNoReplyNeeded,
    ActionType.CloseConversation,
    ActionType.CreateTask,
    ActionType.SendReply,
    ActionType.SetPriority,
  ],
  [TriggerType.TopicsAdded]: [ActionType.SendReply, ActionType.SetPriority],
};
